import { useCallback, useEffect, useState } from "react";
import { MessageSquare, ShieldOff } from "lucide-react";

import { cn } from "../../utils/tailwindUtils";
import useApiMethods from "../../lib/useApiMethods";
import { APPRAISAL_FEEDBACK } from "../../constants/api";

/**
 * Read-only 360 feedback for one appraisal.
 *
 * Masking is the API's job, not this component's: an anonymous reviewer
 * arrives already named "Anonymous" with no id. The only thing rendered from
 * `is_masked` is the icon, so there is nothing here that could leak an
 * identity by forgetting a condition.
 *
 * The API also refuses to serve this to the employee until their appraisal is
 * published, so an empty panel here is a legitimate state rather than an error.
 */
const FeedbackSummary = ({ appraisalId }) => {
    const { fetch } = useApiMethods();
    const [rows, setRows] = useState([]);
    const [progress, setProgress] = useState(null);
    const [loading, setLoading] = useState(false);
    const [denied, setDenied] = useState(false);

    const load = useCallback(async () => {
        if (!appraisalId) return;
        setLoading(true);
        try {
            const res = await fetch(APPRAISAL_FEEDBACK, { appraisal_id: appraisalId });
            if (res?.status === "success") {
                setRows(res.data || []);
                setProgress({
                    total: res.reviewers_total,
                    submitted: res.reviewers_submitted,
                    pending: res.reviewers_pending,
                });
            } else {
                setDenied(true);
            }
        } catch (_) {
            setDenied(true);
        }
        setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appraisalId]);

    useEffect(() => { load(); }, [load]);

    if (denied) return null;

    return (
        <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-2 flex-wrap mb-3">
                <MessageSquare className="h-4 w-4 text-muted-foreground" />
                <h3 className="font-semibold">360&deg; feedback</h3>
                {progress?.total > 0 && (
                    <span className="text-xs text-muted-foreground">
                        {progress.submitted} of {progress.total} responded
                        {progress.pending > 0 && " — reviews are not held up by the rest"}
                    </span>
                )}
            </div>

            {loading ? (
                <p className="text-sm text-muted-foreground">Loading…</p>
            ) : rows.length === 0 ? (
                <p className="text-sm text-muted-foreground">No feedback submitted yet.</p>
            ) : (
                <ul className="space-y-4">
                    {rows.map((r) => (
                        <li key={r.id} className="border-b border-border/60 pb-3 last:border-0 last:pb-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                                {r.is_masked && <ShieldOff className="h-3.5 w-3.5 text-muted-foreground" />}
                                <span className={cn("text-sm font-medium",
                                    r.is_masked && "italic text-muted-foreground")}>
                                    {r.reviewer?.name}
                                </span>
                                <span className="text-xs text-muted-foreground capitalize">
                                    {String(r.relationship || "").replace("_", " ")}
                                </span>
                            </div>

                            {(r.feedback || []).map((f) => (
                                <div key={f.id} className="text-sm mt-2">
                                    {f.goal_name && (
                                        <span className="text-xs text-muted-foreground">On {f.goal_name}: </span>
                                    )}
                                    {/* Shown for context, never summed into the final score --
                                        peer ratings are input to the manager's judgement. */}
                                    {f.rating ? (
                                        <span className="text-xs px-1.5 py-0.5 rounded bg-muted mr-2">
                                            {f.rating}/5
                                        </span>
                                    ) : null}
                                    {f.comment && <p className="mt-1">{f.comment}</p>}
                                    {f.strengths && (
                                        <p className="mt-1 text-muted-foreground">
                                            <span className="font-medium">Strengths:</span> {f.strengths}
                                        </p>
                                    )}
                                    {f.improvements && (
                                        <p className="mt-1 text-muted-foreground">
                                            <span className="font-medium">To improve:</span> {f.improvements}
                                        </p>
                                    )}
                                </div>
                            ))}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default FeedbackSummary;
